/**
 * V113-7-0 — client mirror of the v11.3 monitoring write gates, so pages can disable an action and say
 * who can do it. The server stays the authority (every gate below is enforced there; a stale client
 * role just earns a 403, which monitoringErrorOf() maps).
 *
 *   D-113-27 / ADR-0101 §7 (requireRole('owner','admin','editor')):
 *     verify a method · link a system · change environment · accept a candidate · declare / confirm class
 *     · in / out of scope for a linked system
 *   D-113-28 review H2: out of scope on an item whose bound check FAILS → owner / admin
 *   configuration-items.ts: merge → owner / admin; retire → asset-writer bar (owner / admin, or the
 *     lead of a program that scopes assets)
 *   monitoring-feeds.ts / monitoring-plan.ts / system-anomaly-thresholds.ts: feeds, plan signing and
 *     anomaly thresholds → owner / admin
 *   Name, tag, owner and free tags on a configuration item → any member.
 */
import type { WorkspaceRole } from './workspace';

export type MonitoringAction =
  | 'edit_identity'
  | 'change_environment'
  | 'verify_method'
  | 'link_system'
  | 'set_scope'
  | 'scope_out_failing'
  | 'accept_candidate'
  | 'declare_class'
  | 'merge'
  | 'retire'
  | 'manage_feeds'
  | 'sign_plan'
  | 'edit_anomaly_thresholds';

type Role = WorkspaceRole | string;

const MEMBER: readonly string[] = ['owner', 'admin', 'editor', 'viewer', 'advisor'];
const EDITORS: readonly string[] = ['owner', 'admin', 'editor'];
const ADMINS: readonly string[] = ['owner', 'admin'];

/** Roles allowed per action (retire also admits a program lead; see `leadsAssetProgram`). */
export const MONITORING_ACTION_ROLES: Readonly<Record<MonitoringAction, readonly string[]>> = Object.freeze({
  edit_identity: MEMBER,
  change_environment: EDITORS,
  verify_method: EDITORS,
  link_system: EDITORS,
  set_scope: EDITORS,
  scope_out_failing: ADMINS,
  accept_candidate: EDITORS,
  declare_class: EDITORS,
  merge: ADMINS,
  retire: ADMINS,
  manage_feeds: ADMINS,
  sign_plan: ADMINS,
  edit_anomaly_thresholds: ADMINS,
});

export interface CapabilityContext {
  /** The membership role from useWorkspace(). Assessor sessions never write here. */
  role: Role | null | undefined;
  /** True when the member leads a program that scopes assets (retire's second door). */
  leadsAssetProgram?: boolean;
}

export interface Capability {
  allowed: boolean;
  /** Who can, in plain words, for a disabled action's hint; null when allowed. */
  whoCan: string | null;
}

const ROLE_WORD: Record<string, string> = { owner: 'an owner', admin: 'an admin', editor: 'an editor' };

function joinOr(words: string[]): string {
  if (words.length <= 1) return words[0] ?? '';
  return `${words.slice(0, -1).join(', ')} or ${words[words.length - 1]}`;
}

/** "Only an owner, admin or editor can do this." style text for a role list. */
export function whoCanText(roles: readonly string[], extra?: string): string {
  const words = roles.map((r) => ROLE_WORD[r] ?? r);
  // Collapse the articles after the first: "an owner, admin or editor".
  const [first, ...rest] = words;
  const list = joinOr([first ?? '', ...rest.map((w) => w.replace(/^an? /, ''))].concat(extra ? [extra] : []).filter(Boolean));
  return `Only ${list} can do this.`;
}

export function can(action: MonitoringAction, ctx: CapabilityContext): Capability {
  const roles = MONITORING_ACTION_ROLES[action];
  const role = ctx.role ?? '';
  const allowed = roles.includes(role) || (action === 'retire' && !!ctx.leadsAssetProgram && MEMBER.includes(role));
  if (allowed) return { allowed: true, whoCan: null };
  return { allowed: false, whoCan: whoCanText(roles, action === 'retire' ? 'the lead of a program that scopes assets' : undefined) };
}

/** Scope for one (item, system): out of scope on a FAILING item needs the stricter gate. */
export function canSetScope(scope: 'in_scope' | 'out_of_scope', failing: boolean, ctx: CapabilityContext): Capability {
  return can(scope === 'out_of_scope' && failing ? 'scope_out_failing' : 'set_scope', ctx);
}

/**
 * D-113-27/28 (V113-7): the two out-of-scope cases by name. Both need a reason (the server answers 400
 * `reason_required`); a failing item answers 403 `failing_requires_owner_admin` for an editor.
 */
export const scopeOutPassing = (ctx: CapabilityContext): Capability => canSetScope('out_of_scope', false, ctx);
export const scopeOutFailing = (ctx: CapabilityContext): Capability => canSetScope('out_of_scope', true, ctx);

/**
 * PATCH on a configuration item: identity fields are any member; an environment change is the editor
 * bar. Pass the fields that would change.
 */
export function canPatchConfigurationItem(changes: { environment?: boolean }, ctx: CapabilityContext): Capability {
  return can(changes.environment ? 'change_environment' : 'edit_identity', ctx);
}

/** Every action at once, for a page that renders several buttons. */
export function monitoringCapabilities(ctx: CapabilityContext): Record<MonitoringAction, Capability> {
  const out = {} as Record<MonitoringAction, Capability>;
  for (const a of Object.keys(MONITORING_ACTION_ROLES) as MonitoringAction[]) out[a] = can(a, ctx);
  return out;
}
